/**
 * Tender Workflow Service
 * Handles persistent workflow state management for tender creation process
 * Enables users to resume tender creation from where they left off
 */

import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';

// Workflow step definitions
// Workflow step definitions
/**
 * ⚠️ RECOMMENDED (6) WAS REMOVED AND EVERYTHING AFTER IT MOVED DOWN ONE.
 *
 * The step displayed Compare & Select's own picks and wrote nothing the proposal reads. The
 * numbers here are what `tender.current_step` and `completed_steps` hold — migration 167 shifts
 * the rows that were already saved. The `workflow_data` KEYS did not move with them; see
 * stepDataKey for why.
 */
export const WORKFLOW_STEPS = {
  CLIENT_SITES: 1,
  RETAILER_COMM: 2,
  EMAIL_PROCESSING: 3,
  OFFERS: 4,
  COMPARE: 5,
  TEMPLATE: 6,
  DETAILS: 7,
  OVERVIEW: 8
} as const;

export type WorkflowStep = typeof WORKFLOW_STEPS[keyof typeof WORKFLOW_STEPS];

// Workflow data structure
export interface TenderWorkflowData {
  step1?: {
    clientData?: any;
    sites?: any[];
    tenderName?: string;
    selectedClientId?: string;
    completed?: boolean;
  };
  step2?: {
    // Retailer Communication
    completed?: boolean;
    offers?: any[]; // Legacy support
  };
  step3?: {
    // Email Processing
    completed?: boolean;
    stagedData?: any;
  };
  step4?: {
    offers?: any[];
    completed?: boolean;
    recommendedOffers?: any[]; // Legacy support
  };
  step5?: {
    // Compare & Select
    completed?: boolean;
    selectedTemplate?: any; // Legacy support
    referenceDocument?: any; // Legacy support
  };
  /**
   * ⚠️ NO LONGER WRITTEN. The Recommended Offer step was removed — it displayed Compare &
   * Select's picks and wrote nothing the proposal reads. 154 tenders carry this key from before
   * the removal; nothing loads it, and it is left in place rather than migrated away.
   */
  step6?: {
    recommendedOffers?: any[];
    completed?: boolean;
    offerDetailsData?: any; // Legacy support
  };
  step7?: {
    selectedTemplate?: any;
    referenceDocument?: any;
    completed?: boolean;
    documentData?: any; // Legacy support
  };
  step8?: {
    offerDetailsData?: any;
    usageOverride?: {
      peak_kwh: number;
      offpeak_kwh: number;
      shoulder_kwh: number;
      total_kwh: number;
    };
    completed?: boolean;
  };
  step9?: {
    documentData?: any;
    completed?: boolean;
  };
}

// Tender summary for dashboard
export interface TenderSummary {
  id: string;
  opportunity_name: string;
  client_name?: string;
  current_step: number;
  completed_steps: number[];
  progress_percentage: number;
  last_accessed_at: string;
  created_at: string;
  status: string;
  site_count?: number;
  retailers_sent_count?: number;
  retailers_replied_count?: number;
  retailers_total_count?: number;
  last_email_sent_at?: string | null;
  first_reply_received_at?: string | null;
}

/**
 * Which `workflow_data` key a step writes to.
 *
 * ⚠️ THE KEYS DID NOT MOVE WHEN THE STEPS DID.
 *
 * Removing the Recommended Offer step shifted template, offer details and overview from 7/8/9 to
 * 6/7/8. Writing those under `step6`/`step7`/`step8` would have collided with 1,103 tenders of
 * saved state — `step6` already holds the old step's `recommendedOffers` on 154 of them, and a
 * loader reading `step6` for a template would find offers there.
 *
 * So the storage keys stay exactly where they were and only the numbering moved. The cost is
 * this map; the alternative was a jsonb rename across every tender in production, which can only
 * be got wrong once.
 */
const STEP_DATA_KEY: Record<number, keyof TenderWorkflowData> = {
  1: 'step1',
  2: 'step2',
  3: 'step3',
  4: 'step4',
  5: 'step5',
  6: 'step7', // Template Selection — was step 7
  7: 'step8', // Offer Details     — was step 8
  8: 'step9', // Document Overview — was step 9
};

/** The `workflow_data` key for a step, falling back to `step{n}` for anything unmapped. */
export const stepDataKey = (step: number): keyof TenderWorkflowData =>
  STEP_DATA_KEY[step] ?? (`step${step}` as keyof TenderWorkflowData);

/**
 * Save tender workflow state to database
 */
export const saveTenderWorkflowState = async (
  tenderId: string,
  currentStep: WorkflowStep,
  stepData: any,
  isStepCompleted: boolean = false
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Fetch current state and prepare update in one roundtrip sequence
    // (we need current workflow_data to merge, so fetch is unavoidable — but we minimise fields)
    const { data: currentTender, error: fetchError } = await supabase
      .from('tender')
      .select('workflow_data, completed_steps')
      .eq('id', tenderId)
      .single();

    if (fetchError) throw new Error(`Failed to fetch tender: ${fetchError.message}`);

    const currentWorkflowData = (currentTender?.workflow_data as TenderWorkflowData) || {};
    const stepKey = stepDataKey(currentStep);

    currentWorkflowData[stepKey] = {
      ...currentWorkflowData[stepKey],
      ...stepData,
      completed: isStepCompleted,
    };

    let completedSteps = currentTender?.completed_steps || [];
    if (isStepCompleted && !completedSteps.includes(currentStep)) {
      completedSteps = [...completedSteps, currentStep].sort();
    }

    const now = new Date().toISOString();
    const { error: updateError } = await supabase
      .from('tender')
      .update({
        current_step: currentStep,
        completed_steps: completedSteps,
        workflow_data: currentWorkflowData,
        last_accessed_at: now,
        updated_at: now,
      })
      .eq('id', tenderId);

    if (updateError) throw new Error(`Failed to update tender: ${updateError.message}`);

    console.log(`✅ Saved workflow state for tender ${tenderId}, step ${currentStep}`);
    return { success: true };

  } catch (error: any) {
    console.error('❌ Error saving tender workflow state:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Load tender workflow state from database
 */
export const loadTenderWorkflowState = async (
  tenderId: string
): Promise<{
  success: boolean;
  data?: {
    currentStep: WorkflowStep;
    completedSteps: number[];
    workflowData: TenderWorkflowData;
    tenderInfo: any;
    sites?: any[];
  };
  error?: string;
}> => {
  try {
    const { data: tender, error } = await supabase
      .from('tender')
      .select(`
        *,
        client:client_id (
          id,
          name,
          abn
        ),
        tender_site (*, site:site_id (document_file_paths, retailer_notes))
      `)
      .eq('id', tenderId)
      .single();

    if (error) {
      throw new Error(`Failed to load tender: ${error.message}`);
    }

    return {
      success: true,
      data: {
        currentStep: tender.current_step,
        completedSteps: tender.completed_steps || [],
        workflowData: tender.workflow_data || {},
        tenderInfo: tender,
        sites: tender.tender_site || []
      }
    };

  } catch (error: any) {
    console.error('❌ Error loading tender workflow state:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Persist the tender's contract-term series — which month count each Year N is.
 *
 * Lives on `tender.extn.term_series` rather than a column of its own so this needs no
 * migration. Merged into whatever else extn holds, never replacing it.
 *
 * ⚠️ The series is what stops a term being labelled by snapping to the ORG-WIDE
 * sys_config.requested_contract_terms list. A tender that asked for 4/16/28/40 had nowhere to
 * record that, so 4 and 16 both snapped to the 12-month bucket and collided on Year 1. See
 * lib/tenderTermSeries.ts for the full account.
 */
export const saveTenderTermSeries = async (
  tenderId: string,
  series: Record<string, number | string>,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Read-modify-write on the jsonb: extn carries unrelated keys and a blind overwrite
    // would drop them.
    const { data: current, error: fetchError } = await supabase
      .from('tender')
      .select('extn')
      .eq('id', tenderId)
      .single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn : {};

    const { error: updateError } = await supabase
      .from('tender')
      .update({ extn: { ...extn, term_series: series }, updated_at: new Date().toISOString() })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save term series: ${updateError.message}`);

    console.log(`✅ Saved term series for tender ${tenderId}:`, series);
    return { success: true };
  } catch (error: any) {
    console.error('❌ Error saving tender term series:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get list of user's tenders for dashboard
 */
export const getUserTenders = async (): Promise<{
  success: boolean;
  data?: TenderSummary[];
  error?: string;
}> => {
  try {
    // Get auth user and org in parallel
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data: orgMember } = await supabase
      .from('base_organisation_members')
      .select('organisation_id')
      .eq('user_id', user.id)
      .single();

    if (!orgMember) throw new Error('User organization not found');

    const { data: tenders, error } = await supabase
      .from('tender')
      .select(`
        id,
        opportunity_name,
        current_step,
        completed_steps,
        last_accessed_at,
        created_at,
        status,
        retailers_sent_count,
        retailers_replied_count,
        retailers_total_count,
        last_email_sent_at,
        first_reply_received_at,
        client:client_id (
          name
        ),
        tender_site (count)
      `)
      .eq('org_id', orgMember.organisation_id)
      .order('last_accessed_at', { ascending: false })
      .limit(50);

    if (error) {
      throw new Error(`Failed to fetch tenders: ${error.message}`);
    }

    // Calculate progress percentage for each tender
    const tendersWithProgress: TenderSummary[] = (tenders || []).map(tender => {
      const completedSteps = tender.completed_steps || [];
      // ⚠️ THE DENOMINATOR WAS 6 AGAINST A NINE-STEP WIZARD, so a tender six steps in showed
      // 100% with three to go. Counting WORKFLOW_STEPS keeps it honest and moves with the
      // wizard — it is now 8, since the Recommended Offer step was removed. Capped because
      // completed_steps on an older tender can still carry a step number that no longer exists.
      const totalSteps = Object.keys(WORKFLOW_STEPS).length;
      const progressPercentage = Math.min(
        100,
        Math.round((completedSteps.length / totalSteps) * 100),
      );

      return {
        id: tender.id,
        opportunity_name: tender.opportunity_name || 'Untitled Tender',
        client_name: (tender.client as any)?.name,
        current_step: tender.current_step,
        completed_steps: completedSteps,
        progress_percentage: progressPercentage,
        last_accessed_at: tender.last_accessed_at,
        created_at: tender.created_at,
        status: tender.status,
        site_count: tender.tender_site?.[0]?.count || 0,
        retailers_sent_count: tender.retailers_sent_count || 0,
        retailers_replied_count: tender.retailers_replied_count || 0,
        retailers_total_count: tender.retailers_total_count || 0,
        last_email_sent_at: tender.last_email_sent_at,
        first_reply_received_at: tender.first_reply_received_at
      };
    });

    return { success: true, data: tendersWithProgress };

  } catch (error: any) {
    console.error('❌ Error fetching user tenders:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create new tender with initial workflow state
 */
export const createNewTender = async (
  clientId: string,
  tenderName: string,
  initialData?: any
): Promise<{ success: boolean; tenderId?: string; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get user's organization
    const { data: orgMember } = await supabase
      .from('base_organisation_members')
      .select('organisation_id')
      .eq('user_id', user.id)
      .single();

    if (!orgMember) {
      throw new Error('User organization not found');
    }

    // Create tender with initial workflow state
    const { data: tender, error } = await supabase
      .from('tender')
      .insert({
        org_id: orgMember.organisation_id,
        client_id: clientId,
        owner_user_id: user.id,
        opportunity_name: tenderName,
        status: 'draft',
        current_step: 1,
        completed_steps: [],
        workflow_data: initialData || {},
        last_accessed_at: new Date().toISOString()
      })
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to create tender: ${error.message}`);
    }

    console.log(`✅ Created new tender: ${tender.id}`);
    return { success: true, tenderId: tender.id };

  } catch (error: any) {
    console.error('❌ Error creating new tender:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete tender
 */
export const deleteTender = async (
  tenderId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('tender')
      .delete()
      .eq('id', tenderId);

    if (error) {
      throw new Error(`Failed to delete tender: ${error.message}`);
    }

    console.log(`✅ Deleted tender: ${tenderId}`);
    return { success: true };

  } catch (error: any) {
    console.error('❌ Error deleting tender:', error);
    return { success: false, error: error.message };
  }
};

/**
 * The consultant whose name and contact details print on the proposal's last page.
 *
 * ⚠️ WHY THIS IS STORED ON THE TENDER AND NOT DERIVED AT GENERATION TIME.
 *
 * Generation resolved it in this order: the Salesforce tender owner at
 * `tender.extn.consultant`, and failing that the `base_profiles` row of WHOEVER CLICKED
 * GENERATE. That second branch is wrong twice over.
 *
 * It is wrong for the client: an admin regenerating someone else's tender silently replaces the
 * consultant's contact details with their own, and the client is told to ring the wrong person.
 *
 * It is wrong because it cannot work. Measured on prod: 272 of 899 tenders carry no Salesforce
 * consultant at all, and every one of the 15 rows in base_profiles has an email but NO phone —
 * so for those tenders the phone could never be filled from any source. A further 89 tenders
 * have a Salesforce consultant with an email and no phone. That is 361 tenders, 40%, where
 * {consultant_mobile} and {consultant_phone} had nothing to resolve to.
 *
 * Captured on Step 1 instead, pre-filled from the same two sources, so the common case still
 * costs no typing and the value is fixed to the TENDER rather than to the person exporting it.
 *
 * Lives on `tender.extn.consultant` — the key generation already reads first, so no new
 * plumbing and no migration. Merged into extn, never replacing it.
 */
export type TenderConsultant = {
  name: string;
  email: string;
  phone: string;
};

/**
 * F1 — the names the team give their groups.
 *
 * Their words: *"We can be the ones to name each group."* A group is a set of sites a retailer
 * prices as one deal; the system works out which sites those are, and the team say what to call
 * the result.
 *
 * Stored on `tender.extn.groups` as `{ "<group key>": "<name>" }`, beside the consultant, the
 * term series and the cover override — no schema change. The key is the one
 * `lib/siteGroups.siteGroupKey` builds, so a name follows its group as long as the group exists;
 * edit a site's ABN, state or contract date and it belongs to a different group, which is
 * correct — it is a different deal, and it has no name until someone gives it one.
 */
export const getTenderGroupNames = async (tenderId: string): Promise<Record<string, string>> => {
  try {
    const { data, error } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (error) throw new Error(error.message);
    const stored = (data?.extn as Record<string, unknown> | null)?.groups;
    if (!stored || typeof stored !== 'object') return {};
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(stored as Record<string, unknown>)) {
      if (typeof value === 'string' && value.trim()) out[key] = value.trim();
    }
    return out;
  } catch (error: unknown) {
    console.error('❌ Error reading tender group names:', error);
    return {};
  }
};

/**
 * Name one group, or clear its name with an empty string.
 *
 * Read-modify-write, like the consultant: `extn` carries unrelated keys and the other groups'
 * names, and a blind overwrite would drop them.
 */
export const saveTenderGroupName = async (
  tenderId: string,
  groupKey: string,
  name: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: current, error: fetchError } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn as Record<string, unknown> : {};
    const groups = (extn.groups && typeof extn.groups === 'object')
      ? { ...(extn.groups as Record<string, unknown>) } : {};

    const trimmed = name.trim();
    if (trimmed) groups[groupKey] = trimmed;
    else delete groups[groupKey];

    const { error: updateError } = await supabase
      .from('tender')
      .update({ extn: { ...extn, groups }, updated_at: new Date().toISOString() })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save group name: ${updateError.message}`);

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender group name:', message);
    return { success: false, error: message };
  }
};

/**
 * F9 — the name the cover is addressed to, when the team have overridden it.
 *
 * Their words: *"If multi-site has multiple entity names, we want to make the 'Prepared for:'
 * customizable."* A tender covering several legal entities has no single Client record that is
 * the truthful answer, so the broker states it.
 *
 * Stored on `tender.extn.cover.preparedFor` — the same jsonb column the consultant and the term
 * series already live in, so this needs no schema change. Absent means "use the Client record",
 * which is every tender that has not been overridden.
 */
export const getTenderPreparedFor = async (tenderId: string): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (error) throw new Error(error.message);
    const cover = (data?.extn as Record<string, unknown> | null)?.cover as
      | Record<string, unknown> | null | undefined;
    const value = cover && typeof cover === 'object' ? cover.preparedFor : null;
    return typeof value === 'string' ? value.trim() : '';
  } catch (error: unknown) {
    console.error('❌ Error reading tender "Prepared for":', error);
    return '';
  }
};

/**
 * Persist the cover's "Prepared for" name. An empty string CLEARS the override, so the cover
 * goes back to the Client record rather than being stuck on a name the user has just deleted.
 */
export const saveTenderPreparedFor = async (
  tenderId: string,
  preparedFor: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Read-modify-write: extn carries the consultant and the term series too.
    const { data: current, error: fetchError } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn as Record<string, unknown> : {};
    const cover = (extn.cover && typeof extn.cover === 'object') ? extn.cover as Record<string, unknown> : {};
    const trimmed = preparedFor.trim();

    const { error: updateError } = await supabase
      .from('tender')
      .update({
        extn: { ...extn, cover: { ...cover, preparedFor: trimmed || null } },
        updated_at: new Date().toISOString(),
      })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save "Prepared for": ${updateError.message}`);

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender "Prepared for":', message);
    return { success: false, error: message };
  }
};

/** Read the consultant stored on a tender. Absent keys come back as empty strings. */
export const getTenderConsultant = async (
  tenderId: string,
): Promise<TenderConsultant | null> => {
  try {
    const { data, error } = await supabase
      .from('tender')
      .select('extn')
      .eq('id', tenderId)
      .single();
    if (error) throw new Error(error.message);

    const stored = (data?.extn as Record<string, unknown> | null)?.consultant as
      | Record<string, unknown> | null | undefined;
    if (!stored || typeof stored !== 'object') return null;

    const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
    return {
      name: str(stored.name),
      email: str(stored.email),
      // Salesforce carries these as separate fields and either may be the one populated.
      phone: str(stored.mobile) || str(stored.phone),
    };
  } catch (error: unknown) {
    console.error('❌ Error reading tender consultant:', error);
    return null;
  }
};

/**
 * Persist the consultant onto the tender.
 *
 * `phone` is written to BOTH `phone` and `mobile` because generation reads
 * `(mobile ?? phone)` and the proposal template has separate {consultant_mobile} and
 * {consultant_phone} tokens; one number answers both rather than asking for the same digits
 * twice.
 */
export const saveTenderConsultant = async (
  tenderId: string,
  consultant: TenderConsultant,
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Read-modify-write: extn carries unrelated keys and a blind overwrite would drop them.
    const { data: current, error: fetchError } = await supabase
      .from('tender')
      .select('extn')
      .eq('id', tenderId)
      .single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn : {};
    const existing = (extn as Record<string, unknown>).consultant;
    const merged = {
      ...(existing && typeof existing === 'object' ? existing : {}),
      name: consultant.name.trim(),
      email: consultant.email.trim(),
      phone: consultant.phone.trim(),
      mobile: consultant.phone.trim(),
    };

    const { error: updateError } = await supabase
      .from('tender')
      .update({ extn: { ...extn, consultant: merged }, updated_at: new Date().toISOString() })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save consultant: ${updateError.message}`);

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender consultant:', message);
    return { success: false, error: message };
  }
};

/**
 * The two dates that fix a tender's first contract year.
 *
 * `start` — the day the new contract begins. Normally the day after the earliest
 * `current_contract_expiry` across the sites, which is what the engine derives on its own. A new
 * connection has no prior contract to expire, so there is nothing to derive from and the broker
 * states it.
 *
 * `yearOneEnd` — the day that first year ends. A contract starting mid-month has two defensible
 * answers and the client's own workbooks use both: AMITY counts to the anniversary, COWES
 * BAKEHOUSE counts to the month end. See `lib/yearOneEnd.ts`.
 *
 * Both are `YYYY-MM-DD`, or `''` for "not stated".
 */
export type TenderContractYear = {
  start: string;
  yearOneEnd: string;
};

export const EMPTY_CONTRACT_YEAR: TenderContractYear = { start: '', yearOneEnd: '' };

const isoOrBlank = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * Read the tender's stated contract year.
 *
 * Stored on `tender.extn.contractYear` — the same jsonb column the consultant, the group names
 * and the cover override already live on, so this needs no schema change. Blank fields mean
 * "work it out exactly as before", which is every tender until somebody answers on Step 1.
 */
export const getTenderContractYear = async (tenderId: string): Promise<TenderContractYear> => {
  try {
    const { data, error } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (error) throw new Error(error.message);
    const block = (data?.extn as Record<string, unknown> | null)?.contractYear as
      | Record<string, unknown> | null | undefined;
    if (!block || typeof block !== 'object') return { ...EMPTY_CONTRACT_YEAR };
    return {
      start: isoOrBlank(block.start),
      yearOneEnd: isoOrBlank(block.yearOneEnd),
    };
  } catch (error: unknown) {
    console.error('❌ Error reading tender contract year:', error);
    return { ...EMPTY_CONTRACT_YEAR };
  }
};

/**
 * Pin the contract year to the tender. An empty string CLEARS that date, so the engine goes back
 * to deriving it rather than being stuck on something someone has just deleted.
 *
 * Read-modify-write: `extn` carries the consultant, the group names, the term series and the
 * cover override, and a blind overwrite would drop every one of them.
 */
export const saveTenderContractYear = async (
  tenderId: string,
  contractYear: TenderContractYear,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data: current, error: fetchError } = await supabase
      .from('tender').select('extn').eq('id', tenderId).single();
    if (fetchError) throw new Error(`Failed to read tender extn: ${fetchError.message}`);

    const extn = (current?.extn && typeof current.extn === 'object') ? current.extn as Record<string, unknown> : {};
    const block = (extn.contractYear && typeof extn.contractYear === 'object')
      ? extn.contractYear as Record<string, unknown> : {};

    const { error: updateError } = await supabase
      .from('tender')
      .update({
        extn: {
          ...extn,
          contractYear: {
            ...block,
            start: contractYear.start.trim() || null,
            yearOneEnd: contractYear.yearOneEnd.trim() || null,
          },
        },
        updated_at: new Date().toISOString(),
      })
      .eq('id', tenderId);
    if (updateError) throw new Error(`Failed to save contract year: ${updateError.message}`);

    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender contract year:', message);
    return { success: false, error: message };
  }
};

/** The address the proposal cover prints, held on the tender's own columns. */
export type TenderCoverAddress = {
  street: string;
  city: string;
  state: string;
  postcode: string;
};

export const EMPTY_COVER_ADDRESS: TenderCoverAddress = { street: '', city: '', state: '', postcode: '' };

/**
 * The tender's own address — what the cover's "Address:" line prints.
 *
 * ⚠️ REAL COLUMNS, NOT `extn`. `tender.addr_street / addr_city / addr_state / addr_postcode`
 * have existed all along and `EmailCompositionDialog` already prefers them over the first
 * site's. Nothing ever rendered an input for them, so across prod's 1,099 tenders all 1,099 are
 * blank and the cover fell back to whichever site the AI happened to pick — on a 20-site tender
 * across four states, one shop's street address.
 *
 * Filling them here fixes the cover and the retailer emails from one box.
 */
export const getTenderCoverAddress = async (tenderId: string): Promise<TenderCoverAddress> => {
  try {
    const { data, error } = await supabase
      .from('tender')
      .select('addr_street, addr_city, addr_state, addr_postcode')
      .eq('id', tenderId).single();
    if (error) throw new Error(error.message);
    return {
      street: (data?.addr_street ?? '').toString().trim(),
      city: (data?.addr_city ?? '').toString().trim(),
      state: (data?.addr_state ?? '').toString().trim(),
      postcode: (data?.addr_postcode ?? '').toString().trim(),
    };
  } catch (error: unknown) {
    console.error('❌ Error reading tender cover address:', error);
    return { ...EMPTY_COVER_ADDRESS };
  }
};

/**
 * Persist the cover address. Written as nulls when blank rather than empty strings, so the
 * existing `.filter(Boolean)` joins in the email dialogs keep behaving as they do today.
 */
export const saveTenderCoverAddress = async (
  tenderId: string,
  address: TenderCoverAddress,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const clean = (v: string) => v.trim() || null;
    const { error } = await supabase
      .from('tender')
      .update({
        addr_street: clean(address.street),
        addr_city: clean(address.city),
        addr_state: clean(address.state),
        addr_postcode: clean(address.postcode),
        updated_at: new Date().toISOString(),
      })
      .eq('id', tenderId);
    if (error) throw new Error(`Failed to save cover address: ${error.message}`);
    return { success: true };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Error saving tender cover address:', message);
    return { success: false, error: message };
  }
};
